import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Stack,
  IconButton,
  Collapse,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';

export default function QuestLineTreeView({ questLines: providedQuestLines, onSelectQuestLine, selectedQuestLineId }) {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [copySuccess, setCopySuccess] = useState('');

  // Use provided quest lines or fetch if not provided
  const { data: fetchedQuestLines = [], isLoading } = useQuery({
    queryKey: ['questLines'],
    queryFn: async () => {
      const response = await api.get('/quests/quest-lines');
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: !providedQuestLines
  });

  const questLines = providedQuestLines || fetchedQuestLines;

  // Parse requirements helper
  const parseRequirements = (requirements) => {
    try {
      if (typeof requirements === 'string') {
        return JSON.parse(requirements);
      }
      return requirements || [];
    } catch {
      return [];
    }
  };

  // Build tree structure
  const treeData = useMemo(() => {
    if (!questLines.length) return { roots: [], nodeMap: new Map() };

    const nodeMap = new Map();
    
    // Initialize all nodes
    questLines.forEach(ql => {
      nodeMap.set(ql.id, {
        questLine: ql,
        children: [],
        parents: []
      });
    });

    // Build parent-child relationships based on requirements
    questLines.forEach(ql => {
      const requirements = parseRequirements(ql.requirements);
      const questLineRequirements = requirements.filter(req => req.dataPoint === 'QuestLineComplete');
      
      questLineRequirements.forEach(req => {
        const parentNode = nodeMap.get(req.targetId);
        const childNode = nodeMap.get(ql.id);
        
        if (parentNode && childNode) {
          parentNode.children.push(childNode);
          childNode.parents.push(parentNode);
        }
      });
    });

    // Find root nodes (no parents or parents with requirements)
    const roots = Array.from(nodeMap.values()).filter(node => node.parents.length === 0);

    return { roots, nodeMap };
  }, [questLines]);

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const handleCopyGuid = async (guid, e) => {
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(guid);
      setCopySuccess('GUID copied to clipboard!');
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopySuccess('Failed to copy GUID');
      setTimeout(() => setCopySuccess(''), 3000);
    }
  };

  const renderNode = (node, level = 0) => {
    const { questLine, children } = node;
    const isExpanded = expandedNodes.has(questLine.id);
    const hasChildren = children.length > 0;
    const isActive = questLine.isActive === 1 || questLine.isActive === true;
    const isSelected = selectedQuestLineId === questLine.id;
    const requirements = parseRequirements(questLine.requirements);
    const nonQuestLineReqs = requirements.filter(req => req.dataPoint !== 'QuestLineComplete');

    // Background colors for different levels - alternating shades
    const levelBgColors = [
      'transparent', // Level 0
      'rgba(255, 255, 255, 0.02)', // Level 1
      'rgba(255, 255, 255, 0.04)', // Level 2
      'rgba(255, 255, 255, 0.06)', // Level 3
      'rgba(255, 255, 255, 0.08)', // Level 4+
    ];
    const bgColor = levelBgColors[Math.min(level, levelBgColors.length - 1)];

    return (
      <Box key={questLine.id}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: level * 2 + 1.5, // More indentation per level
            py: 1,
            pr: 1.5,
            cursor: 'pointer',
            bgcolor: isSelected ? 'action.selected' : bgColor,
            '&:hover': {
              bgcolor: isSelected ? 'action.selected' : level > 0 ? 'rgba(255, 255, 255, 0.08)' : 'action.hover'
            },
            borderLeft: level > 0 ? '3px solid' : 'none',
            borderLeftColor: level > 0 
              ? (level % 2 === 1 
                  ? 'rgba(25, 118, 210, 0.3)' 
                  : 'rgba(156, 39, 176, 0.3)') 
              : 'transparent',
            ml: level > 0 ? 1 : 0,
            transition: 'background-color 0.2s ease'
          }}
          onClick={() => onSelectQuestLine(questLine)}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(questLine.id);
              }}
              sx={{ mr: 0.5, p: 0.5 }}
            >
              {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 28, mr: 0.5 }} />
          )}
          
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: isActive ? '#10b981' : '#ef4444',
              flexShrink: 0,
              mr: 1
            }}
          />
          
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography 
              variant="body2" 
              fontWeight={isSelected ? 'bold' : 'medium'}
              sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {questLine.displayName}
            </Typography>
            
            <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
              <Chip
                label={questLine.questLineType || 'story'}
                size="small"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
              {!isActive && (
                <Chip
                  label="Inactive"
                  size="small"
                  color="error"
                  sx={{ height: '18px', fontSize: '0.65rem' }}
                />
              )}
              {hasChildren && (
                <Chip
                  label={`${children.length} unlock${children.length !== 1 ? 's' : ''}`}
                  size="small"
                  color="primary"
                  sx={{ height: '18px', fontSize: '0.65rem' }}
                />
              )}
              {nonQuestLineReqs.length > 0 && (
                <Tooltip title={nonQuestLineReqs.map(req => {
                  if (req.dataPoint === 'TycoonBuilderLevelReached') {
                    return `Tycoon Power: ${req.amount || req.targetId || '?'}`;
                  }
                  return req.dataPoint;
                }).join(', ')}>
                  <Chip
                    label={`+${nonQuestLineReqs.length} req`}
                    size="small"
                    color="warning"
                    sx={{ height: '18px', fontSize: '0.65rem' }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Box>
          
          <Tooltip title="Copy GUID">
            <IconButton
              size="small"
              onClick={(e) => handleCopyGuid(questLine.id, e)}
              sx={{ ml: 0.5 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            {children.map(child => renderNode(child, level + 1))}
          </Collapse>
        )}
      </Box>
    );
  };

  if (!providedQuestLines && isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (treeData.roots.length === 0) {
    return (
      <Box sx={{ px: 1.5, py: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No quest lines found matching the selected filter.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ px: 1.5, pb: 1 }}>
        {treeData.roots.length} root questline{treeData.roots.length !== 1 ? 's' : ''}
      </Typography>
      {treeData.roots.map(node => renderNode(node))}
      
      <Snackbar
        open={!!copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopySuccess('')} severity="success" sx={{ width: '100%' }}>
          {copySuccess}
        </Alert>
      </Snackbar>
    </Box>
  );
}

